import { Component, OnInit } from '@angular/core';
import { AdminService } from '../../../services/admin/admin.service';

@Component({
  selector: 'app-plan-manager',
  templateUrl: './plan-manager.component.html',
  styleUrls: ['./plan-manager.component.scss']
})
export class PlanManagerComponent implements OnInit {
  activeTab: 'user' | 'broker' | 'merchant' = 'user';
  plans: any[] = [];
  brokerPlans: any[] = [];
  merchantPlans: any[] = [];
  editingPlanId: string | null = null;
  editDraft: any = {};
  saving = false;
  savedPlanId: string | null = null;

  editingBrokerPlanId: string | null = null;
  editBrokerDraft: any = {};
  savingBroker = false;
  savedBrokerPlanId: string | null = null;

  editingMerchantPlanId: string | null = null;
  editMerchantDraft: any = {};
  savingMerchant = false;
  savedMerchantPlanId: string | null = null;

  featureToggles = [
    { key: 'contactView', label: 'Contact View' },
    { key: 'viewAnnualEarnings', label: 'Annual Earnings View' },
    { key: 'profileBoost', label: 'Profile Boost' },
    { key: 'chatAccess', label: 'Chat Access' },
    { key: 'unlimitedSearch', label: 'Unlimited Search' },
    { key: 'advancedFilters', label: 'Advanced Filters' },
    { key: 'unlimitedInterests', label: 'Unlimited Interests' },
    { key: 'horoscopeDownload', label: 'Horoscope Download' },
    { key: 'galleryView', label: 'Gallery View' },
    { key: 'whoViewedMyProfile', label: 'Who Viewed My Profile' },
    { key: 'featuredProfile', label: 'Featured VIP Badge' },
    { key: 'aiSuggestions', label: 'AI Suggestions' },
  ];

  constructor(private adminService: AdminService) {}

  ngOnInit(): void {
    this.loadPlans();
    this.loadBrokerPlans();
    this.loadMerchantPlans();
  }

  loadPlans(): void {
    this.adminService.getPlans().subscribe({
      next: res => { if (res.status === 'success') this.plans = res.data; },
      error: err => console.error('[PlanManager]', err)
    });
  }

  startEdit(plan: any): void {
    this.editingPlanId = plan._id;
    const f = plan.features || {};
    const limits = f.limits || {};
    const comm = f.communication || {};
    const disc = f.discovery || {};
    const prem = f.premium || {};

    this.editDraft = {
      dailyProfileLimit: limits.dailyProfileLimit ?? 20,
      monthlyInterestLimit: limits.monthlyInterestLimit ?? 10,
      boostMonthlyLimit: limits.boostMonthlyLimit ?? 0,
      boostCooldownDays: limits.boostCooldownDays ?? 0,
      savedFilters: limits.savedFilters ?? 5,
      favoritesLimit: limits.favoritesLimit ?? 10,
      
      contactView: !!comm.contactView,
      viewAnnualEarnings: !!comm.viewAnnualEarnings,
      chatAccess: !!comm.chatAccess,
      
      unlimitedSearch: !!disc.unlimitedSearch,
      advancedFilters: !!disc.advancedFilters,
      aiSuggestions: !!disc.aiSuggestions,
      galleryView: !!disc.galleryView,
      whoViewedMyProfile: !!disc.whoViewedMyProfile,
      
      profileBoost: !!prem.profileBoost,
      featuredProfile: !!prem.featuredProfile,
      horoscopeDownload: !!prem.horoscopeDownload,
      unlimitedInterests: !!prem.unlimitedInterests
    };
    this.savedPlanId = null;
  }

  cancelEdit(): void {
    this.editingPlanId = null;
    this.editDraft = {};
  }

  savePlan(planId: string): void {
    this.saving = true;
    const structuredFeatures = {
      limits: {
        dailyProfileLimit: Number(this.editDraft.dailyProfileLimit) || 0,
        monthlyInterestLimit: Number(this.editDraft.monthlyInterestLimit) || 0,
        savedFilters: Number(this.editDraft.savedFilters) || 0,
        favoritesLimit: Number(this.editDraft.favoritesLimit) || 0,
        boostMonthlyLimit: Number(this.editDraft.boostMonthlyLimit) || 0,
        boostCooldownDays: Number(this.editDraft.boostCooldownDays) || 0
      },
      communication: {
        chatAccess: !!this.editDraft.chatAccess,
        contactView: !!this.editDraft.contactView,
        viewAnnualEarnings: !!this.editDraft.viewAnnualEarnings
      },
      discovery: {
        unlimitedSearch: !!this.editDraft.unlimitedSearch,
        advancedFilters: !!this.editDraft.advancedFilters,
        aiSuggestions: !!this.editDraft.aiSuggestions,
        galleryView: !!this.editDraft.galleryView,
        whoViewedMyProfile: !!this.editDraft.whoViewedMyProfile
      },
      premium: {
        profileBoost: !!this.editDraft.profileBoost,
        featuredProfile: !!this.editDraft.featuredProfile,
        horoscopeDownload: !!this.editDraft.horoscopeDownload,
        unlimitedInterests: !!this.editDraft.unlimitedInterests
      }
    };

    this.adminService.updatePlan(planId, { features: structuredFeatures }).subscribe({
      next: res => {
        this.saving = false;
        if (res.status === 'success') {
          const idx = this.plans.findIndex(p => p._id === planId);
          if (idx !== -1) this.plans[idx] = res.data;
          this.savedPlanId = planId;
          this.editingPlanId = null;
          setTimeout(() => this.savedPlanId = null, 3000);
        }
      },
      error: err => {
        this.saving = false;
        console.error('[PlanManager save]', err);
        alert('Save failed: ' + (err?.error?.message || 'Server error'));
      }
    });
  }

  getFeatureDisplay(plan: any): { label: string; value: string; on?: boolean; off?: boolean }[] {
    const f = plan.features || {};
    const limits = f.limits || {};
    const comm = f.communication || {};
    const disc = f.discovery || {};
    const prem = f.premium || {};
    const bool = (v: any) => !!v;

    return [
      { label: 'Daily Profile Limit', value: limits.dailyProfileLimit > 1000 ? 'Unlimited' : `${limits.dailyProfileLimit ?? 0}/day` },
      { label: 'Monthly Interest Limit', value: limits.monthlyInterestLimit > 1000 ? 'Unlimited' : `${limits.monthlyInterestLimit ?? 0}/mo` },
      { label: 'Profile Boost', value: bool(prem.profileBoost) ? `${limits.boostMonthlyLimit ?? 0} boosts/mo` : 'Not Available', on: bool(prem.profileBoost), off: !bool(prem.profileBoost) },
      { label: 'Boost Cooldown', value: `${limits.boostCooldownDays || 0} days` },
      { label: 'Chat Access', value: bool(comm.chatAccess) ? 'Enabled' : 'Locked', on: bool(comm.chatAccess), off: !bool(comm.chatAccess) },
      { label: 'Contact View', value: bool(comm.contactView) ? 'Unlocked' : 'Locked', on: bool(comm.contactView), off: !bool(comm.contactView) },
      { label: 'Annual Earnings View', value: bool(comm.viewAnnualEarnings) ? 'Unlocked' : 'Locked', on: bool(comm.viewAnnualEarnings), off: !bool(comm.viewAnnualEarnings) },
      { label: 'Gallery View', value: bool(disc.galleryView) ? 'Clear media' : 'Blurred preview', on: bool(disc.galleryView), off: !bool(disc.galleryView) },
      { label: 'Horoscope Download', value: bool(prem.horoscopeDownload) ? 'Enabled' : 'No', on: bool(prem.horoscopeDownload), off: !bool(prem.horoscopeDownload) },
      { label: 'Who Viewed Me', value: bool(disc.whoViewedMyProfile) ? 'Enabled' : 'No', on: bool(disc.whoViewedMyProfile), off: !bool(disc.whoViewedMyProfile) },
      { label: 'Favorites Limit', value: limits.favoritesLimit > 1000 ? 'Unlimited' : `${limits.favoritesLimit ?? 0}` },
      { label: 'Saved Filters', value: `${limits.savedFilters || 0}` },
      { label: 'Advanced Filters', value: bool(disc.advancedFilters) ? 'Enabled' : 'Disabled', on: bool(disc.advancedFilters), off: !bool(disc.advancedFilters) },
    ];
  }

  // ─── Broker Plan Management ────────────────────────────────────────
  loadBrokerPlans(): void {
    this.adminService.getBrokerPlans().subscribe({
      next: res => { if (res.status === 'success') this.brokerPlans = res.data; },
      error: err => console.error('[PlanManager Broker]', err)
    });
  }

  startEditBroker(plan: any): void {
    this.editingBrokerPlanId = plan._id;
    const f = plan.features || {};
    this.editBrokerDraft = {
      price: plan.price ?? 0,
      max_profiles: f.max_profiles ?? 5,
      lead_contact_access: !!f.lead_contact_access,
      who_viewed_candidate: !!f.who_viewed_candidate,
      featured_listings: !!f.featured_listings,
      analytics: !!f.analytics,
      priority_support: !!f.priority_support
    };
    this.savedBrokerPlanId = null;
  }

  cancelEditBroker(): void {
    this.editingBrokerPlanId = null;
    this.editBrokerDraft = {};
  }

  saveBrokerPlan(planId: string): void {
    this.savingBroker = true;
    const updates = {
      price: Number(this.editBrokerDraft.price) || 0,
      features: {
        max_profiles: Number(this.editBrokerDraft.max_profiles) || 5,
        lead_contact_access: !!this.editBrokerDraft.lead_contact_access,
        who_viewed_candidate: !!this.editBrokerDraft.who_viewed_candidate,
        featured_listings: !!this.editBrokerDraft.featured_listings,
        analytics: !!this.editBrokerDraft.analytics,
        priority_support: !!this.editBrokerDraft.priority_support
      }
    };
    this.adminService.updateBrokerPlan(planId, updates).subscribe({
      next: res => {
        this.savingBroker = false;
        if (res.status === 'success') {
          this.savedBrokerPlanId = planId;
          this.loadBrokerPlans();
          setTimeout(() => {
            this.cancelEditBroker();
            this.savedBrokerPlanId = null;
          }, 1500);
        }
      },
      error: () => { this.savingBroker = false; }
    });
  }

  loadMerchantPlans(): void {
    this.adminService.getMerchantPlans().subscribe({
      next: (res: any) => { if (res.status === 'success') this.merchantPlans = res.data; },
      error: (err: any) => console.error('[PlanManager]', err)
    });
  }

  startMerchantEdit(plan: any): void {
    this.editingMerchantPlanId = plan._id;
    this.editMerchantDraft = {
      price: plan.price ?? 0,
      maxServices: plan.maxServices ?? 1,
      maxImagesPerService: plan.maxImagesPerService ?? 5,
      allowsVideoReels: !!plan.allowsVideoReels,
      allowsAds: !!plan.allowsAds,
      hasPremiumBadge: !!plan.hasPremiumBadge,
      directPhoneLeads: !!plan.directPhoneLeads,
      dedicatedAccountManager: !!plan.dedicatedAccountManager
    };
    this.savedMerchantPlanId = null;
  }

  cancelMerchantEdit(): void {
    this.editingMerchantPlanId = null;
    this.editMerchantDraft = {};
  }

  saveMerchantPlan(planId: string): void {
    this.savingMerchant = true;
    const updates = {
      price: Number(this.editMerchantDraft.price) || 0,
      maxServices: Number(this.editMerchantDraft.maxServices) || 1,
      maxImagesPerService: Number(this.editMerchantDraft.maxImagesPerService) || 5,
      allowsVideoReels: !!this.editMerchantDraft.allowsVideoReels,
      allowsAds: !!this.editMerchantDraft.allowsAds,
      hasPremiumBadge: !!this.editMerchantDraft.hasPremiumBadge,
      directPhoneLeads: !!this.editMerchantDraft.directPhoneLeads,
      dedicatedAccountManager: !!this.editMerchantDraft.dedicatedAccountManager
    };
    
    this.adminService.updateMerchantPlan(planId, updates).subscribe({
      next: (res: any) => {
        this.savingMerchant = false;
        if (res.status === 'success') {
          this.savedMerchantPlanId = planId;
          this.loadMerchantPlans();
          setTimeout(() => {
            this.cancelMerchantEdit();
            this.savedMerchantPlanId = null;
          }, 1500);
        }
      },
      error: () => { this.savingMerchant = false; }
    });
  }

  getMerchantFeatureDisplay(plan: any): any[] {
    return [
      { label: 'Max Services', value: plan.maxServices === 999 ? 'Unlimited' : plan.maxServices },
      { label: 'Max Images / Service', value: plan.maxImagesPerService === 999 ? 'Unlimited' : plan.maxImagesPerService },
      { label: 'Video Reels', value: plan.allowsVideoReels ? 'Enabled' : 'Disabled', on: plan.allowsVideoReels, off: !plan.allowsVideoReels },
      { label: 'Sponsored Ads', value: plan.allowsAds ? 'Enabled' : 'Disabled', on: plan.allowsAds, off: !plan.allowsAds },
      { label: 'Premium Badge', value: plan.hasPremiumBadge ? 'Yes' : 'No', on: plan.hasPremiumBadge, off: !plan.hasPremiumBadge },
      { label: 'Direct Phone Leads', value: plan.directPhoneLeads ? 'Yes' : 'No', on: plan.directPhoneLeads, off: !plan.directPhoneLeads },
      { label: 'Account Manager', value: plan.dedicatedAccountManager ? 'Yes' : 'No', on: plan.dedicatedAccountManager, off: !plan.dedicatedAccountManager },
    ];
  }
}
